import React from "react";
import {
  Box,
  Flex,
  Image,
  Heading,
  Text,
  Button,
  Icon,
} from "@chakra-ui/react";
import { Phone, BookmarkPlus, Calendar1 } from "lucide-react";
import { PatientOverview } from "@/types";
import { useBreakpointValue } from "@chakra-ui/react";

interface PatientCardProps {
  patient: PatientOverview;
  onBookAppointment: () => void;
}

export const PatientCard: React.FC<PatientCardProps> = ({
  patient,
  onBookAppointment,
}) => {
  const isMobile = useBreakpointValue({ base: true, md: false });

  return (
    <Box
      bgImage="url('/assets/img/patient bg.png')"
      bgSize="cover"
      bgPosition="center"
      bgRepeat="no-repeat"
      p={{ base: 3, sm: 4, md: 6 }}
      mb={4}
      position="relative"
      width="100%"
      height={{ base: "auto", md: "auto" }}
      rounded={{ base: "md", md: "lg" }}
      overflow="hidden"
    >
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        zIndex={1}
      />

      <Flex
        direction={{ base: "column", sm: "row" }}
        gap={{ base: 3, sm: 4, md: 6 }}
        position="relative"
        zIndex={2}
        align={{ base: "flex-start", sm: "center" }}
      >
        {/* Patient Image */}
        <Image
          src={patient.profile_picture_url || "/assets/img/profile.jpg"}
          alt={patient.user_fullname}
          boxSize={{ base: "70px", sm: "80px", md: "100px", lg: "120px" }}
          rounded={{ base: "md", md: "lg" }}
          shadow="md"
          flexShrink={0}
        />

        {/* Patient Info */}
        <Box flex={1} minW={0}>
          <Text
            fontSize={{ base: "xs", sm: "13px", md: "14px" }}
            color="#2E37A4"
            mb={1}
            fontWeight="bold"
          >
            #{patient.id}
          </Text>
          <Heading
            fontWeight="700"
            fontSize={{ base: "15px", sm: "16px", md: "18px" }}
            mb={1}
            color="gray.900"
            lineHeight="tight"
          >
            {patient.user_fullname}
          </Heading>
          <Text
            fontSize={{ base: "xs", sm: "13px", md: "14px" }}
            color="gray.500"
            mb={3}
            lineHeight="tight"
          >
            {patient.addresses?.primary?.city || "N/A"},{" "}
            {patient.addresses?.primary?.state || "N/A"},{" "}
            {patient.addresses?.primary?.country || "N/A"},{" "}
            {patient.addresses?.primary?.pincode || "N/A"}
          </Text>

          {/* Contact and Action Row */}
          <Flex
            direction={{ base: "column", sm: "row" }}
            align={{ base: "stretch", sm: "center" }}
            justify="space-between"
            gap={{ base: 2, sm: 4 }}
            flexWrap="wrap"
          >
            {/* Contact Info */}
            <Flex
              align="center"
              gap={{ base: 3, sm: 4 }}
              flexWrap={{ base: "wrap", sm: "nowrap" }}
            >
              <Flex align="center" gap={1}>
                <Icon
                  as={Phone}
                  boxSize={{ base: 3, sm: 3.5 }}
                  color="gray.900"
                />
                <Text
                  fontSize={{ base: "xs", sm: "sm" }}
                  fontWeight="400"
                  color="gray.500"
                >
                  Phone:
                </Text>
                <Text
                  fontSize={{ base: "xs", sm: "sm" }}
                  color="gray.900"
                  fontWeight="400"
                  ml={1}
                >
                  {patient.user_phone_number}
                </Text>
              </Flex>

              <Flex align="center" gap={1}>
                <Icon
                  as={Calendar1}
                  boxSize={{ base: 3, sm: 3.5 }}
                  color="gray.900"
                />
                <Text
                  fontSize={{ base: "xs", sm: "sm" }}
                  fontWeight="400"
                  color="gray.500"
                >
                  Last Visited:
                </Text>
                <Text
                  fontSize={{ base: "xs", sm: "sm" }}
                  color="gray.900"
                  fontWeight="400"
                  ml={1}
                >
                  {patient.last_appointment_formatted || "N/A"}
                </Text>
              </Flex>
            </Flex>

            {/* Book Appointment Button */}
            <Button
              leftIcon={<BookmarkPlus size={isMobile ? 14 : 16} />}
              bg="#DC3611"
              color="white"
              size={{ base: "xs", sm: "sm" }}
              _hover={{ bg: "#bf3c1f" }}
              shadow="md"
              px={{ base: 3, sm: 4 }}
              minW={{ base: "100%", sm: "auto" }}
              w={{ base: "100%", sm: "auto" }}
              order={{ base: -1, sm: 0 }}
              onClick={onBookAppointment}
            >
              Book Appointment
            </Button>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
};
